<script setup lang="ts">
import type { Operator } from "~domain/operators/models/operator-model.ts";

import { IconoirProvider } from "@iconoir/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
import { storeToRefs } from "pinia";
import { type PrimeVueLocaleOptions, usePrimeVue, useToast } from "primevue";
import { computed, onErrorCaptured, provide, watch } from "vue";
import { THEME_KEY as ECHARTS_THEME_KEY } from "vue-echarts";
import { ELocale } from "~application/constants/locale.constant.ts";
import { DomainError, DomainErrorCode } from "~domain/global/errors/domain-error.ts";
import primevueEn from "~ui/@application/locales/primevue/en.json";
import primevueFr from "~ui/@application/locales/primevue/fr.json";
import { useTypedI18n } from "~ui/@core/composables/i18n/use-typesafe-i18n.ts";
import { useSentry } from "~ui/@core/composables/sentry/use-sentry.ts";
import { useApplicationStore, type UseApplicationStoreReturn } from "~ui/@core/stores/application/application-store.ts";
import { useOperatorStore } from "~ui/@core/stores/auth/operator-store.ts";

import Toast from "primevue/toast";

const { captureException, setUser } = useSentry();
const applicationStore = useApplicationStore();
const {
  locale,
  isDarkMode,
}: {
  locale: UseApplicationStoreReturn["locale"];
  isDarkMode: UseApplicationStoreReturn["isDarkMode"];
} = storeToRefs(applicationStore);

const operatorStore = useOperatorStore();
const { operator } = storeToRefs(operatorStore);

const i18n = useTypedI18n();
const primevue = usePrimeVue();
const toaster = useToast();
const queryClient = useQueryClient();

const echartTheme = computed(() => {
  return isDarkMode.value ? "dark" : null;
});

provide(ECHARTS_THEME_KEY, echartTheme);

watch(
  locale,
  (locale) => {
    document.documentElement.setAttribute("lang", locale);
    // biome-ignore lint/suspicious/noExplicitAny: I know better than you
    i18n.locale.value = locale as any;

    const primevueLocaleMap: Record<ELocale, PrimeVueLocaleOptions> = {
      [ELocale.FRENCH]: primevueFr,
      [ELocale.ENGLISH]: primevueEn,
    };

    primevue.config.locale = primevueLocaleMap[locale];

    queryClient.invalidateQueries({
      type: "active",
    });
  },
  { immediate: true },
);

onErrorCaptured((err) => {
  console.error("🔥 Uncaught error", err);

  if (err instanceof DomainError) {
    toaster.add({
      severity: "error",
      life: 10_000,
      summary: i18n.t(i18n.k.common.errors[err.errorCode]),
      detail: err.message,
    });
  } else {
    toaster.add({
      severity: "error",
      life: 10_000,
      summary: i18n.t(i18n.k.common.errors[DomainErrorCode.UNEXPECTED_ERROR]),
      detail: err.message,
    });
  }

  captureException(err);

  return false;
});

watch(
  operator,
  (operator?: Operator) => {
    setUser(operator);
  },
  { immediate: true },
);
</script>

<template>
  <IconoirProvider
    :icon-props="{
      color: isDarkMode ? '#CCC' : '#333',
      'stroke-width': 2,
      width: '18px',
      height: '18px',
    }"
  >
    <router-view />
  </IconoirProvider>

  <VueQueryDevtools button-position="bottom-right" />
  <Toast />
</template>

<style scoped></style>
